// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type EtfFilters } from '@shared/data-access/interfaces/api/ai/etf';
import type { MutualFundFilters } from '@shared/data-access/interfaces/api/ai/mutual-funds';

type FormatFilter = Pick<
  MutualFundFilters & EtfFilters,
  | 'min_expenses_ratio'
  | 'max_expenses_ratio'
  | 'min_nav'
  | 'max_nav'
  | 'max_aum'
  | 'min_aum'
>;

const filterValues: { [key: string]: { [range: string]: number | undefined } } = {
  min_nav: {
    '+300M': 300e6,
    '100M - 300M': 100e6,
    '50M - 100M': 50e6,
    '0M - 50M': 0,
  },
  max_nav: {
    '+300M': undefined,
    '100M - 300M': 300e6,
    '50M - 100M': 100e6,
    '0M - 50M': 50e6,
  },
  min_aum: {
    '+300M': 300e6,
    '100M - 300M': 100e6,
    '50M - 100M': 50e6,
    '0M - 50M': 0,
  },
  max_aum: {
    '+300M': undefined,
    '100M - 300M': 300e6,
    '50M - 100M': 100e6,
    '0M - 50M': 50e6,
  },
  min_expenses_ratio: {
    '0.0% - 0.2%': 0,
    '0.2% - 0.5%': 0.2,
    '0.5% - 1.0%': 0.5,
    '1.0% - 1.5%': 1.0,
    '+1.5%': 1.5,
  },
  max_expenses_ratio: {
    '0.0% - 0.2%': 0.2,
    '0.2% - 0.5%': 0.5,
    '0.5% - 1.0%': 1.0,
    '1.0% - 1.5%': 1.5,
    '+1.5%': undefined,
  },
};

/**
 * Formats the filter value based on the specified filter key.
 *
 * @param filter - The key of the filter.
 * @param value - The value of the filter.
 * @returns The formatted filter value as a number, or undefined if the value is
 *   invalid.
 */
export const formatFilter = (
  filter: keyof FormatFilter,
  value: string | undefined,
): number | undefined =>
  value && filterValues[filter] && filterValues[filter][value] !== undefined
    ? filterValues[filter][value]
    : undefined;
